import { Component, Injectable, Input, OnDestroy, OnInit } from "@angular/core";
import { Subscription } from "rxjs";
import { ExistsIn, SingleOrDefault } from "src/app/_helpers/ArrayExtensionMethodHelper.functions";
import { DocumentActor } from "src/app/models/actors";
import { DocumentModel } from "src/app/models/document.model";
import { SendGroupModel, UpdateSendGroupModel } from "src/app/models/sendgroup.model";
import { RequestStatusService } from "../../../requeststatus.service";
import { ActorSendGroupStatusInformationModel, ActorStatusEnum, ActorStatusInformationModel } from "src/app/models/api/actor-status-information.model";
import { StatusRequest } from "src/app/models/requests";
import { DataTypeEnum } from 'src/app/constants/data-type.enum';
import { formatDate } from "@angular/common";
import { BadgeStatusEnum } from "src/app/constants/badge-status.enum";
import { StringHelper } from "src/app/directives/string-helper";
import { TranslateService } from "@ngx-translate/core";
import { RequestActionTypeEnum, VerifyRequestActionService } from "src/app/services/verify-request-action.service";

const uniqueItemsNonNull = (list: any[], keyFn: any) => list.reduce((resultSet, item) =>
  (v => v !== null && v !== undefined ? resultSet.add(v) : resultSet)
    (typeof keyFn === 'string' ? item[keyFn] : keyFn(item)),
  new Set).size;

@Injectable()
export class RequestStatusSendgroupItemComponent implements OnInit, OnDestroy {
  request: StatusRequest = {} as StatusRequest;
  protected requestSubscription!: Subscription;
  protected actorListSubscription!: Subscription;
  protected _sendgroup!: ActorSendGroupStatusInformationModel;
  sortingAllowed: boolean = false;
  actorsInSendgroup: ActorStatusInformationModel[] = [];
  actorsOfSendgroup: ActorStatusInformationModel[] = [];
  sendgroupStatus!: BadgeStatusEnum;
  stringHelper = new StringHelper();
  dateIsValid: boolean = false;
  sendgroupisInactive: boolean = true;
  completionDateList!: ActorStatusInformationModel[];
  sendgroupCompletionDate!: Date | null;

  public get DataTypeEnum() {
    return DataTypeEnum;
  }

  get sendgroup(): ActorSendGroupStatusInformationModel {
    return this._sendgroup;
  }
  @Input() set sendgroup(val: ActorSendGroupStatusInformationModel) {
    this._sendgroup = val;
    if (this._sendgroup) {
      this.getSendgroupWithGroupedActors();
    }
  }
  @Input() sendgroups: ActorSendGroupStatusInformationModel[] = [];
  @Input() sendgroupDeadlines!: SendGroupModel[];
  @Input() isFirstSendGroup: boolean = false;
  @Input() index: number = 0;


  get GetSendgroupStatus(): BadgeStatusEnum {
    if (!this.sendgroup) {
      return BadgeStatusEnum.Info;
    }
    if (this.sendgroup.isCompleted) {
      return BadgeStatusEnum.Success;
    }
    if (this.sendgroup.isActive) {
      return BadgeStatusEnum.Warning;
    }
    return BadgeStatusEnum.Info;
  }

  // MAIN GETTERS FOR THE SENDGROUP
  get SendGroupExists() {
    return this.sendgroup;
  }
  get IsCompletedSendGroup() {
    return this.SendGroupExists && this.sendgroup.isCompleted;
  }
  get IsActiveSendGroup() {
    return this.SendGroupExists && this.sendgroup.isActive && !this.sendgroup.isCompleted;
  }
  get IsFutureSendGroup() {
    return this.SendGroupExists && !this.sendgroup.isActive;
  }
  // Checkers for NgIfs properties
  get HasNoSendDate() {
    return this.SendGroupExists && !this.sendgroup.isActive && !this.sendgroup.madeActiveDate;
  }
  get HasSendDate() {
    return this.SendGroupExists && this.sendgroup.isActive && this.sendgroup.madeActiveDate;
  }
  get HasDeadline() {
    return this.SendGroupExists && this.sendgroup.isActive && this.sendgroup.deadline;
  }
  get HasDeadlineDays() {
    return this.SendGroupExists && !this.sendgroup.isActive && this.sendgroup.deadlineDays;
  }
  get HasCompletionDate() {
    return this.SendGroupExists && this.sendgroup.isCompleted && this.sendgroup.completionDate;
  }

  // ACTION PROPERTIES
  get HasAnySendGroupActionEnabled() {
    // to display dropdown, SendGroup exists, and any action is true:
    return this.SendGroupExists &&
      (
        this.AllowsChangingSendGroupName ||
        this.AllowsDeadlineEdit ||
        this.AllowsDeadlineDaysEdit ||
        this.AllowsResendInvites
      );
  }
  get AllowsChangingSendGroupName() {
    return this.SendGroupExists && !this.IsCompletedSendGroup && this.verifyRequestActionService.IsAllowed(this.request, RequestActionTypeEnum.EditSendGroup);
  }
  get AllowsDeadlineEdit() {
    return this.SendGroupExists && this.sendgroup.allowedToChangeDeadline && this.verifyRequestActionService.IsAllowed(this.request, RequestActionTypeEnum.EditDeadline);
  }
  get AllowsDeadlineDaysEdit() {
    return this.IsFutureSendGroup && this.sendgroup.allowedToChangeDeadlineDays && this.verifyRequestActionService.IsAllowed(this.request, RequestActionTypeEnum.EditDeadline);
  }
  get AllowsResendInvites() {
    return this.SendGroupExists && this.sendgroup.allowedToResendInvite && this.verifyRequestActionService.IsAllowed(this.request, RequestActionTypeEnum.SendReminder);
  }

  public editName() {
    let editSendgroup = new UpdateSendGroupModel;
    editSendgroup.sendGroupGuid = this.sendgroup.sendGroupGuid;
    editSendgroup.sendGroupName = this.sendgroup.sendGroupName;
    this.requestStatusService.openStatusEditSendgroupName(editSendgroup);

  }
  public editDeadline() {
    let editSendgroup = new UpdateSendGroupModel;
    editSendgroup.sendGroupGuid = this.sendgroup.sendGroupGuid;
    editSendgroup.sendGroupName = this.sendgroup.sendGroupName;
    editSendgroup.deadline = this.sendgroup.deadline;
    this.requestStatusService.openSendGroupChangeDeadlineModal(editSendgroup);
  }
  public editDeadlineDays() {
    let editSendgroup = new UpdateSendGroupModel;
    editSendgroup.sendGroupGuid = this.sendgroup.sendGroupGuid;
    editSendgroup.sendGroupName = this.sendgroup.sendGroupName;
    editSendgroup.deadlineDays = this.sendgroup.deadlineDays;
    this.requestStatusService.openSendGroupChangeDeadlineDaysModal(editSendgroup);
  }
  public resendInvite() {
    this.requestStatusService.resendRequestInviteForSendGroup(this._sendgroup);
  }

  isCollapsed: boolean = true;
  isLoading: boolean = false;
  public selectedMoment: any;
  translations = <any>{};
  latestDeadline: Date = new Date();
  groupedActors: ActorStatusInformationModel[] = [];
  documents: DocumentModel[] = [];
  actors: ActorSendGroupStatusInformationModel[] = [];
  persons: DocumentActor[] = [];

  constructor(protected requestStatusService: RequestStatusService, protected translateService: TranslateService, protected verifyRequestActionService: VerifyRequestActionService) {
    this.requestSubscription = this.requestStatusService.selectedRequest$.subscribe(request => {
      if (Object.keys(request).length === 0) {
        return;
      }
      this.request = request;
    });
  }
  ngOnDestroy(): void {
    if (this.requestSubscription) {
      this.requestSubscription.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.actorListSubscription = this.requestStatusService.actors$.subscribe(actors => {
      this.actors = actors;
    });
    this.actorsOfSendgroup = this.sendgroup.actors;
    this.checkSendgroupisInactive();

    // Get sendgroup complitiondate, last date of dateSigned
    this.completionDateList = this.sendgroup.actors.sort((a: any, b: any) => a.completionDate - b.completionDate);
    this.sendgroupCompletionDate = this.completionDateList[this.completionDateList.length - 1].completionDate;
  }

  public getSendgroupWithGroupedActors() {
    let actorsOfSendgroup = this.sendgroup.actors;

    actorsOfSendgroup.forEach(action => {
      if (actorsOfSendgroup.filter(s => s.personGuid === action.personGuid).length > 1) {
        if (!this.groupedActors.map(actor => actor.personGuid).includes(action.personGuid)) {
          let actionsById = actorsOfSendgroup.filter(s => s.personGuid === action.personGuid);
          var uniqueDocumentCount = uniqueItemsNonNull(actionsById, "documentName");
          action.countOfDocuments = uniqueDocumentCount;
          action.countOfAction = actionsById.length;
          action.grouped = true;
          if (ExistsIn(actionsById, "actorStatus", ActorStatusEnum.Error)) {
            action.overRuledStatus = BadgeStatusEnum.Danger;
          } else if (ExistsIn(actionsById, "actorStatus", ActorStatusEnum.TriggeredSpecialStatus)) {
            action.overRuledStatus = BadgeStatusEnum.Danger;
          } else if (ExistsIn(actionsById, "actorStatus", ActorStatusEnum.Pending)) {
            action.overRuledStatus = BadgeStatusEnum.Warning;
          } else if (ExistsIn(actionsById, "actorStatus", ActorStatusEnum.Waiting)) {
            action.overRuledStatus = BadgeStatusEnum.Info;
          } else if (ExistsIn(actionsById, "actorStatus", ActorStatusEnum.Completed)) {
            action.overRuledStatus = BadgeStatusEnum.Success;
          }
          let completionDateActionList = actionsById.filter(x => x.completionDate).sort((a: any, b: any) => a.completionDate - b.completionDate);
          // we need completion actions, AND we must be marked as SUCCES by only having COMPLETED ACTIONS
          if (completionDateActionList && completionDateActionList.length > 0 && action.overRuledStatus == BadgeStatusEnum.Success) {
            action.maxCompletionDate = completionDateActionList[completionDateActionList.length - 1].completionDate;
          } else {
            action.maxCompletionDate = null;
          }
          this.groupedActors.push(action);
          this.sendgroup.actors.filter(s => s.personGuid === action.personGuid).splice(0);
        }
      } else {
        action.grouped = false;
        action.maxCompletionDate = action.completionDate;
        this.groupedActors.push(action);
        this.sendgroup.actors.filter(s => s.personGuid === action.personGuid).splice(0);
      }

      this.actorsInSendgroup = this.groupedActors;
    });
  }


  checkSendgroupisInactive() {
    this.sendgroupisInactive = this.actorsOfSendgroup.map(p => p.actorStatus).includes(ActorStatusEnum.Waiting);
  }

  allActionsCompleted() {
    return this.actorsOfSendgroup.every(p => p.actorStatus === ActorStatusEnum.Completed);
  }


  calculateLatestDeadline(days: number) {
    if (days < 31) return new Date(new Date(this.latestDeadline!).setDate(new Date(this.latestDeadline!).getDate() + days));
    if (days === 31) return new Date(new Date(this.latestDeadline!).setMonth(new Date(this.latestDeadline!).getMonth() % 12 + 1));
    if (days === 62) return new Date(new Date(this.latestDeadline!).setMonth(new Date(this.latestDeadline!).getMonth() % 12 + 2));
    if (days === 93) return new Date(new Date(this.latestDeadline!).setMonth(new Date(this.latestDeadline!).getMonth() % 12 + 3));
    return new Date();
  }

  public changeSendDate(event: any) {
    this.sendgroup.deadline = event;
  }

  public getPreviousDeadlineAsDate(ordinal: number): Date {
    if (ordinal === 0) {
      return new Date(new Date().setDate(new Date().getDate() + this.sendgroup.deadlineDays!));
    }
    let sendgroupDeadlinePreviousSendGroup = SingleOrDefault(this.sendgroupDeadlines, "order", ordinal - 1);
    let sendgroupDeadlineCurrentSendGroup = SingleOrDefault(this.sendgroupDeadlines, "order", ordinal);

    return new Date(new Date(sendgroupDeadlinePreviousSendGroup!.deadline!).setDate(new Date(sendgroupDeadlinePreviousSendGroup!.deadline!).getDate() + sendgroupDeadlineCurrentSendGroup!.deadlineDays!));
  }

  public getPreviousDeadlineAsString(ordinal: number): string {
    return formatDate(this.getPreviousDeadlineAsDate(ordinal), "dd-MM-yyyy HH:mm", "en-US");
  }


  getActionsByPerson(personGuid: string) {
    return this.actorsOfSendgroup.filter(actor => actor.personGuid === personGuid);
  }

  public collapseCurrentSendgroup(sendGroupId: string) {
    let collapsedSendgroupIndex = this.requestStatusService.collapsedSendgroups.findIndex(s => s === sendGroupId);
    if (collapsedSendgroupIndex > -1) {
      this.requestStatusService.collapsedSendgroups.splice(collapsedSendgroupIndex, 1);
    } else {
      this.requestStatusService.collapsedSendgroups.push(sendGroupId);
    }
  }

  public isCurrentSendGroupCollapsed(sendGroupId: string) {
    return !this.requestStatusService.collapsedSendgroups.includes(sendGroupId);
  }

  public getTranslatedSendGroupName(sendGroupName: String) {
    if (!sendGroupName) {
      return;
    }
    if (sendGroupName.toLocaleLowerCase().startsWith("sendgroup")) {
      let sendGroupStringArray = sendGroupName.split(" ");

      if (sendGroupStringArray.length > 1) {
        return this.translateService.instant('Request.Send.' + sendGroupStringArray[0], { number: sendGroupStringArray[1] });
      } else {
        return this.translateService.instant('Request.Send._' + sendGroupStringArray[0]);
      }
    } else if (sendGroupName.toLocaleLowerCase() === "_sendgroup") {
      return this.translateService.instant('Request.Send.' + sendGroupName);
    }
    return sendGroupName;
  }
}


@Component({
  selector: 'mobile-request-status-sendgroup-item',
  templateUrl: './mobile-request-status-sendgroup-item.component.html',
  styleUrl: './request-status-sendgroup-item.component.scss'
})
export class MobileRequestStatusSendgroupItemComponent extends RequestStatusSendgroupItemComponent {
  override get sendgroup(): ActorSendGroupStatusInformationModel {
    return this._sendgroup;
  }
  @Input() override set sendgroup(val: ActorSendGroupStatusInformationModel) {
    this._sendgroup = val;
    if (this._sendgroup) {
      this.getSendgroupWithGroupedActors();
    }
  }
  @Input() override sendgroups: ActorSendGroupStatusInformationModel[] = [];
  @Input() override sendgroupDeadlines!: SendGroupModel[];
  @Input() override isFirstSendGroup: boolean = false;
  @Input() override index: number = 0;

  constructor(protected override requestStatusService: RequestStatusService, protected override translateService: TranslateService, protected override verifyRequestActionService: VerifyRequestActionService) {
    super(requestStatusService, translateService, verifyRequestActionService);
  }

}


@Component({
  selector: 'desktop-request-status-sendgroup-item',
  templateUrl: './desktop-request-status-sendgroup-item.component.html',
  styleUrl: './request-status-sendgroup-item.component.scss'
})
export class DesktopRequestStatusSendgroupItemComponent extends RequestStatusSendgroupItemComponent {
  override get sendgroup(): ActorSendGroupStatusInformationModel {
    return this._sendgroup;
  }
  @Input() override set sendgroup(val: ActorSendGroupStatusInformationModel) {
    this._sendgroup = val;
    if (this._sendgroup) {
      this.getSendgroupWithGroupedActors();
    }
  }
  @Input() override sendgroups: ActorSendGroupStatusInformationModel[] = [];
  @Input() override sendgroupDeadlines!: SendGroupModel[];
  @Input() override isFirstSendGroup: boolean = false;
  @Input() override index: number = 0;

  constructor(protected override requestStatusService: RequestStatusService, protected override translateService: TranslateService, protected override verifyRequestActionService: VerifyRequestActionService) {
    super(requestStatusService, translateService, verifyRequestActionService);
  }
}